import { type ClipboardEvent, type KeyboardEvent } from "react";

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const splitEmails = (raw: string): string[] =>
  raw.split(/[\s,;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);

/**
 * Multi-email staging input: addresses become removable chips on Enter/comma/paste, with the
 * half-typed remainder left in the text box. State is lifted so the parent can fold the pending
 * input into the batch on submit (see `splitEmails`).
 */
export function EmailChipsInput({ emails, onEmailsChange, input, onInputChange, autoFocus }: {
  emails: string[];
  onEmailsChange: (emails: string[]) => void;
  input: string;
  onInputChange: (input: string) => void;
  autoFocus?: boolean;
}) {
  function addEmails(raw: string) {
    const parts = splitEmails(raw);
    if (parts.length === 0) return;
    const next = [...emails];
    const seen = new Set(emails);
    for (const p of parts) {
      if (!seen.has(p)) { seen.add(p); next.push(p); }
    }
    onEmailsChange(next);
    onInputChange("");
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === "," || e.key === ";") {
      e.preventDefault();
      addEmails(input);
    } else if (e.key === "Backspace" && input === "" && emails.length > 0) {
      onEmailsChange(emails.slice(0, -1));
    }
  }

  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData("text");
    if (/[\s,;]/.test(text)) {
      e.preventDefault();
      addEmails(text);
    }
  }

  const invalidEmails = emails.filter((e) => !EMAIL_RE.test(e));

  return (
    <div>
      {emails.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {emails.map((email) => {
            const bad = !EMAIL_RE.test(email);
            return (
              <span
                key={email}
                className="tag"
                style={{ textTransform: "none", letterSpacing: "normal", ...(bad ? { borderColor: "var(--red)", color: "var(--red)" } : {}) }}
              >
                {email}
                <button type="button" className="id-copy" title="Remove" onClick={() => onEmailsChange(emails.filter((x) => x !== email))}>✕</button>
              </span>
            );
          })}
        </div>
      )}
      <div className="inline-form">
        <input
          type="text"
          placeholder="name@company.com — Enter, comma or paste to add"
          value={input}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          spellCheck={false}
          autoFocus={autoFocus}
        />
        <button type="button" className="btn btn-sm" disabled={input.trim() === ""} onClick={() => addEmails(input)}>Add</button>
      </div>
      {invalidEmails.length > 0 && (
        <div className="hint" style={{ color: "var(--red)", marginTop: 5 }}>Invalid: {invalidEmails.join(", ")}</div>
      )}
    </div>
  );
}
